/**
 * Event-driven recent files management
 * Uses globalEventStream instead of callback hell
 */

import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { createEvent } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import { BaseEvent } from '../components/common/AstrologyChart/universal_architecture/types/EventTypes';

/**
 * Recent files events
 */
export type RecentFilesEventType =
  | 'storage.recent-files.updated'    // List needs refresh
  | 'storage.chart.accessed'          // Chart was opened/accessed
  | 'storage.chart.saved'             // Chart was saved
  | 'storage.chart.deleted'           // Chart was deleted
  | 'storage.chart.renamed';          // Chart was renamed

export interface RecentFilesEventData {
  chartId?: string;
  chartName?: string;
  metadata?: any;
  reason?: 'access' | 'save' | 'delete' | 'rename' | 'manual';
}

/**
 * Emit recent files update event
 */
export function emitRecentFilesUpdate(data: RecentFilesEventData = {}): void {
  globalEventStream.emit(createEvent<BaseEvent>(
    'storage.recent-files.updated',
    'RecentFilesManager',
    {
      metadata: {
        timestamp: Date.now(),
        source: 'useRecentFiles',
        ...data,
        ...data.metadata
      }
    }
  ));
}

/**
 * Emit chart accessed event
 */
export function emitChartAccessed(chartId: string, chartName?: string): void {
  globalEventStream.emit(createEvent<BaseEvent>(
    'storage.chart.accessed',
    'RecentFilesManager',
    {
      metadata: {
        timestamp: Date.now(),
        chartId,
        chartName,
        reason: 'access'
      }
    }
  ));

  // Trigger list update
  emitRecentFilesUpdate({ chartId, chartName, reason: 'access' });
}

/**
 * Emit chart saved event
 */
export function emitChartSaved(chartId: string, chartName?: string): void {
  globalEventStream.emit(createEvent<BaseEvent>(
    'storage.chart.saved',
    'RecentFilesManager',
    {
      metadata: {
        timestamp: Date.now(),
        chartId,
        chartName,
        reason: 'save'
      }
    }
  ));

  // Trigger list update
  emitRecentFilesUpdate({ chartId, chartName, reason: 'save' });
}

/**
 * Emit chart deleted event
 */
export function emitChartDeleted(chartId: string, chartName?: string): void {
  globalEventStream.emit(createEvent<BaseEvent>(
    'storage.chart.deleted',
    'RecentFilesManager',
    {
      metadata: {
        timestamp: Date.now(),
        chartId,
        chartName,
        reason: 'delete'
      }
    }
  ));

  // Trigger list update
  emitRecentFilesUpdate({ chartId, chartName, reason: 'delete' });
}

/**
 * Emit chart renamed event
 */
export function emitChartRenamed(chartId: string, newName: string, oldName?: string): void {
  globalEventStream.emit(createEvent<BaseEvent>(
    'storage.chart.renamed',
    'RecentFilesManager',
    {
      metadata: {
        timestamp: Date.now(),
        chartId,
        chartName: newName,
        reason: 'rename',
        oldName
      }
    }
  ));

  // Trigger list update
  emitRecentFilesUpdate({ chartId, chartName: newName, reason: 'rename' });
}

// Legacy functions removed - use pure event-driven approach
// - emitRecentFilesUpdate() instead of refreshRecentFiles()
// - globalEventStream.subscribe('storage.recent-files.updated') instead of registerRecentFilesCallback()
